import { useMemo } from "react";
import {
  Dropdown,
  DropdownOption,
} from "components/designV2/dropdown/dropdown";
import { useTranslation } from "react-i18next";
import {
  PassSettingControlLabel,
  PassSettingControls,
  PassSettingDescription,
  PassSettingLabeledControl,
  PassSettingSection,
  PassSettingText,
  PassSettingTitle,
  SettingsGroupTitle,
  SettingsSubRow,
  SettingsSubRows,
} from "./passSettingsV2.styled";

type Props = {
  value: number;
  onChange: (value: number) => void;
};

const MONTH_VALUE = "0";

const buildOptions = (
  monthLabel: string,
  dayLabel: (day: number) => string,
): DropdownOption[] => [
  { label: monthLabel, value: MONTH_VALUE },
  ...Array.from({ length: 11 }, (_, index) => {
    const value = String(index + 10);
    return { label: dayLabel(Number(value)), value };
  }),
];

const getSelectedHelp = (
  value: number,
  translate: (key: string, options?: Record<string, string | number>) => string,
): { description: string; title: string } => {
  if (value === 0) {
    return {
      title: translate("System.CompanyWorkPeriodDayCutOffMonthTitle"),
      description: translate("System.CompanyWorkPeriodDayCutOffHelpMonth"),
    };
  }

  return {
    title: translate("System.CompanyWorkPeriodDayCutOffDay", { day: value }),
    description: translate("System.CompanyWorkPeriodDayCutOffHelpDay", {
      day: value,
      startDay: value + 1,
      endDay: value,
    }),
  };
};

export const CompanyWorkPeriodDayCutOffSetting = ({
  value,
  onChange,
}: Props): JSX.Element => {
  const { t } = useTranslation();
  const options = useMemo(
    () =>
      buildOptions(t("System.CompanyWorkPeriodDayCutOffMonth"), (day) =>
        t("System.CompanyWorkPeriodDayCutOffDay", { day }),
      ),
    [t],
  );
  const selectedHelp = getSelectedHelp(value, t);

  return (
    <PassSettingSection>
      <PassSettingText>
        <PassSettingTitle>
          {t("System.CompanyWorkPeriodDayCutOff")}
        </PassSettingTitle>
        <PassSettingDescription>
          {t("System.CompanyWorkPeriodDayCutOffInfo")}
        </PassSettingDescription>
      </PassSettingText>

      <SettingsSubRows>
        <SettingsSubRow>
          <PassSettingText>
            <SettingsGroupTitle>{selectedHelp.title}</SettingsGroupTitle>
            <PassSettingDescription>
              {selectedHelp.description}
            </PassSettingDescription>
          </PassSettingText>
          <PassSettingControls>
            <PassSettingLabeledControl>
              <PassSettingControlLabel>
                {t("System.CompanyWorkPeriodDayCutOffField")}*
              </PassSettingControlLabel>
              <Dropdown
                ariaLabel={t("System.CompanyWorkPeriodDayCutOffField")}
                options={options}
                value={String(value)}
                onChange={(nextValue) => onChange(Number(nextValue))}
              />
            </PassSettingLabeledControl>
          </PassSettingControls>
        </SettingsSubRow>
      </SettingsSubRows>
    </PassSettingSection>
  );
};
